import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface MetricRingProps {
  /** 0–1 */
  progress: number
  value: string
  label?: string
  /** Tailwind text color class driving the ring color (stroke uses currentColor) */
  colorClass?: string
  size?: number
  className?: string
}

/**
 * Circular progress ring with a centered rounded-numeral value.
 * The arc sweeps in on mount (CSS transition, reduced-motion safe).
 */
export function MetricRing({
  progress,
  value,
  label,
  colorClass = 'text-primary-600',
  size = 120,
  className,
}: MetricRingProps) {
  const clamped = Math.min(Math.max(progress, 0), 1)
  const strokeWidth = Math.max(size * 0.085, 6)
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const [sweep, setSweep] = useState(0)

  useEffect(() => {
    // Start at 0 and let the CSS transition sweep the arc in.
    const frame = requestAnimationFrame(() => setSweep(clamped))
    return () => cancelAnimationFrame(frame)
  }, [clamped])

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={label ? `${label}: ${value}` : value}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-gray-200"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          stroke="currentColor"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - sweep)}
          className={cn('hearth-ring', colorClass)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={cn(
            'font-rounded font-bold tabular-nums text-gray-900',
            size >= 100 ? 'text-3xl' : 'text-xl'
          )}
        >
          {value}
        </span>
        {label && <span className="text-[11px] text-gray-500">{label}</span>}
      </div>
    </div>
  )
}
